import moment from "moment";
import {
  TableCellType,
  TableHeader,
} from "../../../components/custom-table/table";
import { FilterValues } from "../../../data-model/filterValues";
import { NycbsPharmaOrderModel } from "../../../data-model/nycbsPharmaOrder";
import {
  TableCellButtonWithThreeDots,
  TableCellMultiline,
  TableCellNotesIcon,
  TableCellWithBadge,
} from "../../../data-model/tableCells";
import { CmmOrdersTableDataModel } from "../../../data-model/tablesData";
import {
  getKeyFromEnumValuesForOrderStatus,
  getOrderStatusDisplayText,
  OrderStatus,
} from "../../../enums/authStatus";
import { BadgeColor, BadgeTextColor } from "../../../enums/coloredText";
import {
  NycbsPharmaOrderKeys,
  NycbsPharmaOrderRequiredKeys,
  NycbsPharmaOrderRequiredKeysLabelForUI,
} from "../../../enums/nycbsPharmaOrder";
import { CmmOrderTableColumnKeys } from "../../../enums/tableColumnKeys";
import { calculateAge } from "../../../utils/ageCalculator";
import { filterData } from "../../../utils/filterData";
import { generateFullName } from "../../../utils/generateOrderDataForTable";
import { capitalizeString } from "../../../utils/stringModifications";
import { findLabelFromGivenState } from "../../pharmaPaForm/utils/stateOptions";
import getPoc from "../../pharmaStpFile/table/utils/getPoc";
import { getTableButtonConfig } from "../utils/getButtonLabel";
import { getCorrectTextForHover } from "../utils/getCorrectTextForHover";
import { getPortalNameFromMetadata } from "./utils/getPortalNameFromMetadata";

const extractDateFromFilename = (filename: string): string => {
  const match = filename.match(/(\d{12})\.csv$/);
  if (!match) return "";
  const raw = match[1];
  return moment(raw, "YYYYMMDDHHmm").isValid()
    ? moment(raw, "YYYYMMDDHHmm").format("MM/DD/YYYY")
    : "";
};

/**
 * Returns a trimmed review-reason string for CMM order tooltips (Needs Review column).
 * Accepts snake_case or camelCase field names from the API.
 *
 * @param item - Raw NYCBS / CMM pharma order row
 * @returns Non-empty trimmed text, or undefined when absent
 */
const getReviewReasonForTooltip = (
  item: NycbsPharmaOrderModel,
): string | undefined => {
  const snake = item?.[NycbsPharmaOrderKeys.ReviewReason];
  const camel = item?.["reviewReason"];
  const raw =
    typeof snake === "string" && snake.trim() !== ""
      ? snake
      : typeof camel === "string"
        ? camel
        : "";
  const trimmed = typeof raw === "string" ? raw.trim() : "";
  return trimmed.length > 0 ? trimmed : undefined;
};

export const CmmOrderTableHeader: TableHeader[] = [
  {
    label: "",
    key: "expandableRowIcon",
    order: 0,
    width: 3,
    type: TableCellType.EXPANDABLE_ROW_ICON,
  },
  {
    label: "Input",
    key: CmmOrderTableColumnKeys.SEQUENCE,
    order: 1,
    width: 9,
    sortable: false,
    type: TableCellType.MULTILINE,
    subKey: "mainText",
  },
  {
    label: "Date of Service",
    key: CmmOrderTableColumnKeys.DATE_OF_SERVICE,
    order: 2,
    width: 8,
    sortable: true,
    type: TableCellType.STRING,
  },
  {
    label: "Patient Details",
    key: CmmOrderTableColumnKeys.PATIENT_DETAILS,
    order: 3,
    width: 12,
    sortable: true,
    type: TableCellType.MULTILINE,
    subKey: "mainText",
  },
  {
    label: "Date of Birth",
    key: CmmOrderTableColumnKeys.DATE_OF_BIRTH,
    order: 4,
    width: 10,
    sortable: true,
    type: TableCellType.MULTILINE,
    subKey: "mainText",
  },

  {
    label: "Provider Details",
    key: CmmOrderTableColumnKeys.PROVIDER_DETAILS,
    order: 5,
    width: 10,
    sortable: false,
    type: TableCellType.STRING,
  },
  {
    label: "Drug",
    key: CmmOrderTableColumnKeys.DRUG,
    order: 6,
    width: 12,
    sortable: false,
    type: TableCellType.MULTILINE,
    subKey: "mainText",
  },
  {
    label: "Key",
    key: CmmOrderTableColumnKeys.CMM_KEY,
    order: 7,
    width: 10,
    sortable: false,
    type: TableCellType.COPY_DATA,
  },
  {
    label: "Missing Data",
    key: CmmOrderTableColumnKeys.NO_DATA_FIELDS,
    order: 8,
    width: 7,
    sortable: false,
    type: TableCellType.BADGE,
  },
  {
    label: "CMM Status",
    key: CmmOrderTableColumnKeys.CMM_STATUS,
    order: 9,
    width: 8,
    sortable: false,
    type: TableCellType.BADGE,
  },
  // {
  //   label: "Human Review",
  //   key: CmmOrderTableColumnKeys.HUMAN_REVIEW,
  //   order: 10,
  //   width: 8,
  //   sortable: false,
  //   type: TableCellType.STRING,
  // },

  {
    label: "Portal",
    key: CmmOrderTableColumnKeys.PORTAL,
    order: 10,
    width: 6,
    sortable: false,
    type: TableCellType.BADGE,
  },
  {
    label: "POC",
    key: CmmOrderTableColumnKeys.POC,
    order: 11,
    width: 6,
    sortable: false,
    type: TableCellType.STRING,
  },
  {
    label: "Needs Review",
    key: CmmOrderTableColumnKeys.NEEDS_REVIEW,
    order: 12,
    width: 7,
    sortable: false,
    type: TableCellType.BADGE,
  },
  {
    label: "Action",
    key: CmmOrderTableColumnKeys.ACTION,
    order: 13,
    width: 11,
    sortable: false,
    type: TableCellType.BUTTON_WITH_THREE_DOTS,
  },
  {
    label: "",
    key: CmmOrderTableColumnKeys.ORDER_NOTES_ICON,
    order: 14,
    width: 4,
    sortable: false,
    type: TableCellType.NOTES_ICON,
  },
  // {
  //   label: "CMM",
  //   key: CmmOrderTableColumnKeys.CMM_INPUT_VIEW_ICON,
  //   order: 12,
  //   width: 4,
  //   sortable: false,
  //   type: TableCellType.CMM_INPUT_VIEW_ICON,
  // },
];

export const generateTableDataForCmmOrdersTable = (
  data: NycbsPharmaOrderModel[],
  getDrugColor?: (drugName: string | undefined | null) => string,
): CmmOrdersTableDataModel[] => {
  return data.map((item) => {
    const failedValidationFields: string[] =
      (item?.[NycbsPharmaOrderKeys.FailedValidationFields] as unknown as
        string[] | null) ?? [];
    const hasFailedValidation = failedValidationFields.length > 0;

    const patientDetails: TableCellMultiline = {
      mainText: capitalizeString(
        generateFullName(
          item?.[NycbsPharmaOrderKeys.PatientFirstName] ?? "",
          "",
          item?.[NycbsPharmaOrderKeys.PatientLastName] ?? "",
        ),
      ),
      secondaryText: `MRN : ${item?.[NycbsPharmaOrderKeys.PatientMrn] ?? "--"}`,
    };
    const dateOfBirth: TableCellMultiline = {
      mainText: item?.[NycbsPharmaOrderKeys.PatientDob] ?? "--",
      secondaryText: `${calculateAge(item?.[NycbsPharmaOrderKeys.PatientDob] ?? "--")} yrs`,
      hideCopyIcon: true,
    };
    const createdAtDate = item?.[NycbsPharmaOrderKeys.CreatedAt]
      ? moment(item?.[NycbsPharmaOrderKeys.CreatedAt]).format("MM/DD/YYYY")
      : "--";
    const filenameDate = extractDateFromFilename(item?.["filename"] ?? "");
    const dateOfService: string = createdAtDate;
    const medication: string =
      item?.[NycbsPharmaOrderKeys.DrugNameOncoEmr] ?? "--";
    const providerDetails: string = capitalizeString(
      generateFullName(
        item?.[NycbsPharmaOrderKeys.ProviderFirstName] ?? "",
        "",
        item?.[NycbsPharmaOrderKeys.ProviderLastName] ?? "",
      ),
    );

    // const dateOfBirth: string = item?.[NycbsPharmaOrderKeys.PatientDob] ?? "--";

    const insuranceState: string =
      findLabelFromGivenState(
        item?.[NycbsPharmaOrderKeys.PatientInsuranceState] ?? "",
      ) ?? "--";

    const type = item?.[NycbsPharmaOrderKeys.Type] ?? "--";
    const drug: TableCellMultiline = {
      mainText: item?.[NycbsPharmaOrderKeys.DrugNameOncoEmr] ?? "--",
      secondaryText: type,
      hideCopyIcon: true,
    };

    const id = item?.[NycbsPharmaOrderKeys.Identifier] ?? "";

    const metadata = item?.[NycbsPharmaOrderKeys.Metadata];
    const portalName = getPortalNameFromMetadata(metadata);
    const isEvicore = portalName?.toLowerCase() === "evicore";
    const portal: TableCellWithBadge = {
      text: portalName,
      color: isEvicore
        ? BadgeTextColor["Approved"]
        : BadgeTextColor["First Step Outcome"],
      bgColor: isEvicore
        ? BadgeColor["Approved"]
        : BadgeColor["First Step Outcome"],
    };

    const buttonConfig = getTableButtonConfig(
      item?.[NycbsPharmaOrderKeys.CmmStatus] ?? "",
      hasFailedValidation,
    );

    const button: TableCellButtonWithThreeDots = {
      label: buttonConfig.label,
      buttonId: buttonConfig.buttonId,
      disabled: buttonConfig.disabled ?? false,
      rowId: id,
      threeDotsOptions: [
        {
          id: "rerun_from_onco_emr",
          text: "Rerun from Onco Emr",
        },
        {
          id: "rerun_from_cmm",
          text: "Rerun from CMM",
        },
        {
          id: "report_prescription_inaccuracy",
          text: "Report Prescription Inaccuracy",
        },
        {
          id: "report_medication_inaccuracy",
          text: "Report Medication Inaccuracy",
        },
        {
          id: "report_inaccuracy",
          text: "Report Form Inaccuracy",
        },
      ],
    };

    const cmmStatusKey = getKeyFromEnumValuesForOrderStatus(
      item?.[NycbsPharmaOrderKeys.CmmStatus] ?? "",
    );
    const hoverText = getCorrectTextForHover(item);

    const cmmStatusText = cmmStatusKey
      ? OrderStatus[cmmStatusKey]
      : "No Status";
    const badgeText = {
      text: getOrderStatusDisplayText(cmmStatusText),
      color: BadgeTextColor[cmmStatusText],
      bgColor: BadgeColor[cmmStatusText],
      hoverText: hoverText,
    };

    const noDataFieldsBadge: TableCellWithBadge = {
      text: hasFailedValidation
        ? `Yes (${failedValidationFields.length})`
        : "No",
      color: hasFailedValidation
        ? BadgeTextColor["Error"]
        : BadgeTextColor["Approved"],
      bgColor: hasFailedValidation
        ? BadgeColor["Error"]
        : BadgeColor["Approved"],
      displayText: hasFailedValidation
        ? failedValidationFields.join(", ")
        : "No Missing Fields",
    };
    const needsReviewRaw = item?.[NycbsPharmaOrderKeys.NeedsReview];
    const needsReviewNormalized =
      typeof needsReviewRaw === "string"
        ? needsReviewRaw.trim().toLowerCase()
        : "";
    const needsReviewYes = needsReviewNormalized === "needed";
    const reviewReasonTooltip = getReviewReasonForTooltip(item);
    const needsReviewBadge: TableCellWithBadge = {
      text: needsReviewYes ? "Yes" : "No",
      color: needsReviewYes
        ? BadgeTextColor["Error"]
        : BadgeTextColor["Approved"],
      bgColor: needsReviewYes ? BadgeColor["Error"] : BadgeColor["Approved"],
      hoverText: reviewReasonTooltip,
      displayText: reviewReasonTooltip,
    };

    const cmmKey = item?.[NycbsPharmaOrderKeys.CmmResultKey] ?? "";
    const sequenceValue = item?.[NycbsPharmaOrderKeys.Sequence] ?? "";
    const sequence: TableCellMultiline = {
      mainText: filenameDate || "--",
      secondaryText: sequenceValue ? `SEQ: ${sequenceValue}` : "",
      hideCopyIcon: false,
    };
    const formName = item?.[NycbsPharmaOrderKeys.FormName] ?? "";
    const username = item?.[NycbsPharmaOrderKeys.PortalUsername] ?? "";

    // Additional fields for expandable row
    const drugQuantity = item?.[NycbsPharmaOrderKeys.DrugQuantity] ?? "";
    const drugDaysSupply = item?.[NycbsPharmaOrderKeys.DrugDaysSupply] ?? "";
    const drugQuantityQualifier =
      item?.[NycbsPharmaOrderKeys.DrugQuantityQualifier] ?? "";

    const primaryDiagnoses =
      item?.[NycbsPharmaOrderKeys.PrimaryDiagnoses] ?? "";
    const primaryDiagnosesDescription =
      item?.[NycbsPharmaOrderKeys.PrimaryDiagnosesDescription] ?? "";

    const secondaryDiagnoses =
      item?.[NycbsPharmaOrderKeys.SecondaryDiagnoses] ?? "";
    const secondaryDiagnosesDescription =
      item?.[NycbsPharmaOrderKeys.SecondaryDiagnosesDescription] ?? "";

    const planName = item?.[NycbsPharmaOrderKeys.PlanName] ?? "";
    const patientRxBin = item?.[NycbsPharmaOrderKeys.PatientRxBin] ?? "";
    const patientRxGroup = item?.[NycbsPharmaOrderKeys.PatientRxGroup] ?? "";
    const patientRxPcn = item?.[NycbsPharmaOrderKeys.PatientRxPcn] ?? "";
    const formPickedFlag = item?.[NycbsPharmaOrderKeys.FormPickedFlag] ?? "";
    const patientMemberId = item?.[NycbsPharmaOrderKeys.PatientMemberId] ?? "";
    const drugConfidenceScore =
      item?.[NycbsPharmaOrderKeys.DrugConfidenceScore] ?? "";
    const drugPickedThinking =
      item?.[NycbsPharmaOrderKeys.DrugPickedThinking] ?? "";
    const drugFetchedFrom = item?.[NycbsPharmaOrderKeys.DrugFetchedFrom] ?? "";
    const primaryDiagnosesData =
      item?.[NycbsPharmaOrderKeys.PrimaryDiagnosesData] ?? {};
    const secondaryDiagnosesData =
      item?.[NycbsPharmaOrderKeys.SecondaryDiagnosesData] ?? {};
    const prescriptionData =
      item?.[NycbsPharmaOrderKeys.PrescriptionData] ?? {};
    const drugName = item?.[NycbsPharmaOrderKeys.DrugName] ?? "";
    const drugCohort = item?.[NycbsPharmaOrderKeys.DrugCohort] ?? "";
    const drugClass = item?.[NycbsPharmaOrderKeys.DrugClass] ?? "";
    const genericName = item?.[NycbsPharmaOrderKeys.GenericName] ?? "";
    const brandName = item?.[NycbsPharmaOrderKeys.BrandName] ?? "";
    const isSubstitutionAllowed =
      String(item?.[NycbsPharmaOrderKeys.IsSubstitutionAllowed]) === "true";
    const medicationDecisionResult =
      item?.[NycbsPharmaOrderKeys.MedicationDecisionResult] ?? {};

    const drugNameForColor = item?.[NycbsPharmaOrderKeys.DrugNameOncoEmr];
    const borderColor = getDrugColor ? getDrugColor(drugNameForColor) : "gray";
    const activeInsurance = item?.[NycbsPharmaOrderKeys.ActiveInsurance] ?? {};
    const assigneeId = item?.[NycbsPharmaOrderKeys.AssigneeId] ?? null;
    const poc = getPoc(assigneeId ?? "") || "--";

    const notesRaw = item?.[NycbsPharmaOrderKeys.Notes];
    const notes =
      typeof notesRaw === "string" && notesRaw.trim() !== "" ? notesRaw : null;
    const orderNotesIcon: TableCellNotesIcon = {
      hasNotes: notes !== null,
    };

    return {
      patientDetails,
      dateOfService,
      medication,
      providerDetails,
      dateOfBirth,
      insuranceState,
      drug,
      button,
      [CmmOrderTableColumnKeys.CMM_STATUS]: badgeText,
      id,
      cmmKey,
      formName,
      cmmOrderDeleteIcon: id,
      patientId: item?.[NycbsPharmaOrderKeys.PatientMrn] ?? "",
      rowData: JSON.stringify(item),
      noDataFields: noDataFieldsBadge,
      needsReview: needsReviewBadge,
      username,
      expandableRowIcon: {
        borderColor: borderColor,
        borderWidth: 4,
        id: item?.[NycbsPharmaOrderKeys.Identifier] ?? "",
        isExpanded: false,
      },
      drugQuantity,
      drugDaysSupply,
      drugQuantityQualifier,
      primaryDiagnoses,
      primaryDiagnosesDescription,
      secondaryDiagnoses,
      secondaryDiagnosesDescription,
      planName,
      patientRxBin,
      patientRxGroup,
      patientRxPcn,
      patientMemberId,
      formPickedFlag,
      drugConfidenceScore,
      drugPickedThinking,
      drugFetchedFrom,
      type,
      primaryDiagnosesData,
      secondaryDiagnosesData,
      prescriptionData,
      cmmInputViewIcon: id,
      drugName,
      activeInsurance,
      portal,
      [CmmOrderTableColumnKeys.POC]: poc,
      assignee_id: assigneeId,
      sftpRxBin: item?.[NycbsPharmaOrderKeys.SftpRxBin] ?? "",
      metadata,
      sequence,
      drugCohort,
      drugClass,
      genericName,
      brandName,
      isSubstitutionAllowed,
      medicationDecisionResult,
      failedValidationFields,
      [CmmOrderTableColumnKeys.ORDER_NOTES_ICON]: orderNotesIcon,
      notes,
    };
  });
};

export const generateFilterDataForCmmOrdersTable = (
  data: NycbsPharmaOrderModel[],
  filterValues: FilterValues,
) => {
  const filteredData = filterData(data, filterValues);
  return filteredData;
};

export const generateFieldNamesWhichHaveNoValue = (
  data: NycbsPharmaOrderModel,
) => {
  const fieldNames: string[] = [];
  for (const key in NycbsPharmaOrderRequiredKeys) {
    const valueMapping = NycbsPharmaOrderRequiredKeys[key];
    if (valueMapping === NycbsPharmaOrderKeys.PrimaryDiagnoses) {
      const dataRequiredForPrimaryDiagnosis =
        data?.[NycbsPharmaOrderKeys.IsDiagnosisCodeAvailableOnForm];
      // Defensive check: exclude both string "false" and boolean false
      // While type is string|null, runtime data may contain boolean values
      if (
        dataRequiredForPrimaryDiagnosis !== "false" &&
        // @ts-ignore - Defensive check for boolean despite string|null type
        dataRequiredForPrimaryDiagnosis !== false &&
        (data[valueMapping] === undefined ||
          data?.[valueMapping] === "" ||
          data?.[valueMapping] === null)
      ) {
        fieldNames.push(NycbsPharmaOrderRequiredKeysLabelForUI[key]);
      }
    } else if (valueMapping === NycbsPharmaOrderKeys.FormName) {
      const formName = data?.[valueMapping];
      if (
        formName === undefined ||
        formName === null ||
        formName === "" ||
        formName?.toLowerCase().includes("no forms available")
      ) {
        fieldNames.push(NycbsPharmaOrderRequiredKeysLabelForUI[key]);
      }
    } else if (
      data?.[valueMapping] === undefined ||
      data?.[valueMapping] === null ||
      data?.[valueMapping] === "" ||
      data?.[valueMapping]?.toString()?.trim() === "0"
    ) {
      fieldNames.push(NycbsPharmaOrderRequiredKeysLabelForUI[key]);
    }
  }
  return fieldNames;
};
